/** The edit form works in clock times ("HH:MM", what a time input holds); the day is implied by the booking. */

const pad = (n: number) => String(n).padStart(2, '0');
const DAY_MS = 86_400_000;

/** "14:05" for a time input. */
export function toTimeValue(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The moment with this clock time closest to `reference` (the time being edited), so a booking that crossed
 * midnight keeps the right day: 23:50 → 00:10 moves forward 20 minutes, not back almost a day.
 */
export function atTimeNear(reference: string, time: string): Date {
  const ref = new Date(reference);
  const [h, m] = time.split(':').map(Number);
  const sameDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), h, m).getTime();
  const candidates = [sameDay - DAY_MS, sameDay, sameDay + DAY_MS];
  const best = candidates.reduce((a, b) => (Math.abs(b - ref.getTime()) < Math.abs(a - ref.getTime()) ? b : a));
  return new Date(best);
}

/**
 * The last moment with this clock time at or before `ref`: a booking added afterwards started in the past, so at
 * 11 PM a start of 11:01 AM is this morning, not tomorrow (which would be nearer).
 */
export function atTimeBefore(ref: Date, time: string): Date {
  const [h, m] = time.split(':').map(Number);
  const sameDay = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate(), h, m);
  return sameDay <= ref ? sameDay : new Date(sameDay.getTime() - DAY_MS);
}

/** The first moment with this clock time after `from`: an end time typed as 00:30 for a 23:00 start is the next day. */
export function atTimeAfter(from: Date, time: string): Date {
  const [h, m] = time.split(':').map(Number);
  const sameDay = new Date(from.getFullYear(), from.getMonth(), from.getDate(), h, m);
  return sameDay > from ? sameDay : new Date(sameDay.getTime() + DAY_MS);
}
